import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Paper,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  IconButton,
  Stack,
  Alert,
  Snackbar,
  AlertColor,
  SvgIcon,
  Tooltip
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { visuallyHidden } from '@mui/utils'
import axiosProvider from '../../helpers/AxiosHelper'
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';

interface Data {
  id: number
  name: string
  category: string
}

type Order = 'asc' | 'desc'

interface HeadCell {
  disablePadding: boolean
  id: keyof Data
  label: string
  numeric: boolean
}

interface PairData {
  id: number
  headline: string
  category: string
  isChanged?: boolean // New field to track changes
}

const headCells: readonly HeadCell[] = [
  { id: 'name', numeric: false, disablePadding: false, label: 'Name' },
  { id: 'category', numeric: false, disablePadding: false, label: 'Kategorie' }
]

interface EnhancedTableProps {
  numSelected: number
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Data) => void
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void
  order: Order
  orderBy: keyof Data
  rowCount: number
}

const EnhancedTableHead: React.FC<EnhancedTableProps> = ({
  onSelectAllClick,
  order,
  orderBy,
  numSelected,
  rowCount,
  onRequestSort
}) => {
  const createSortHandler = (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property)
  }

  return (
    <TableHead>
      <TableRow>
        <TableCell padding='checkbox'>
          <Checkbox
            color='primary'
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{
              'aria-label': 'select all desserts'
            }}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}>
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}>
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component='span' sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  )
}

export default function CategoryPair() {
  const [headLines, setHeadLines] = useState<{ id: number; name: string }[]>([])
  const [categories, setCategories] = useState<{ id: number; name: string }[]>([])
  const [pairs, setPairs] = useState<PairData[]>([])
  const [order, setOrder] = useState<Order>('asc')
  const [orderBy, setOrderBy] = useState<keyof Data>('name')
  const [selected, setSelected] = useState<string[]>([])
  const [batchCategory, setBatchCategory] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [newCategoryName, setNewCategoryName] = useState('')
  const [isAdding, setIsAdding] = useState(false)
  const [errorAdding, setErrorAdding] = useState('')
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor | undefined>('info');

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setIsLoading(true);
      setPairs([])
      setHeadLines([])
      setCategories([])

      const res = await axiosProvider.get('headlines-with-groups-and-pairs');
      const { headlines, categories, pair } = res.data;

      if (headlines) {
        setHeadLines(headlines)
      }
      if (categories) {
        setCategories(categories)
      }
      if (pair) {
        setPairs(pair)
      }
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleOpenSnackbar = (message: string, severity: AlertColor) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const handleCloseSnackbar = (
    event: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };


  const handleSaveNewCategory = async () => {
    const lowerCaseNewCategoryName = newCategoryName.toLowerCase()

    if (categories.some((category) => category.name.toLowerCase() === lowerCaseNewCategoryName)) {
      setErrorAdding('This category already exists.')
      return
    }

    try {
      setIsAdding(true)
      setErrorAdding('')
      const response = await axiosProvider.post(`categories`, { category: newCategoryName })
      if (response.data.success) {
        setCategories((prevCategories) => [...prevCategories, { id: prevCategories.length + 1, name: newCategoryName }])
      }
    } catch (error) {
      console.error('Error adding category:', error)
      setErrorAdding('Error adding category.')
    } finally {
      setIsAdding(false)
      setNewCategoryName('')
      setIsModalOpen(false)
    }
  }

  const handleModalOpen = () => {
    setIsModalOpen(true)
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
  }

  const handleBatchCategoryChange = (event: { target: { value: React.SetStateAction<string> } }) => {
    setBatchCategory(event.target.value)
  }

  const applyBatchCategory = () => {
    setPairs((prevPairs) => {
      let updatedPairs = prevPairs.map((pair) =>
        selected.includes(pair.headline) ? { ...pair, category: batchCategory, isChanged: true } : pair
      )
      selected.forEach((headline) => {
        if (!prevPairs.some((pair) => pair.headline === headline)) {
          updatedPairs = [
            ...updatedPairs,
            { id: updatedPairs.length, headline: headline, category: batchCategory, isChanged: true }
          ]
        }
      })

      return updatedPairs
    })
  }

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof Data) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const handleCategoryChange = (headline: string, newCategory: string) => {
    setPairs((prevPairs) => {
      const pairIndex = prevPairs.findIndex((pair) => pair.headline === headline)

      if (pairIndex !== -1) {
        const updatedPairs = [...prevPairs]
        updatedPairs[pairIndex] = { ...prevPairs[pairIndex], category: newCategory, isChanged: true }
        return updatedPairs
      } else {
        return [...prevPairs, { id: prevPairs.length, headline: headline, category: newCategory, isChanged: true }]
      }
    })
  }

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = headLines.map((n) => n.name)
      setSelected(newSelected)
      return
    }
    setSelected([])
  }

  const isSelected = (name: string) => selected.indexOf(name) !== -1

  const handleCheckboxClick = (_event: any, name: string) => {
    const selectedIndex = selected.indexOf(name)
    let newSelected: string[] = []

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name)
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1))
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1))
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1))
    }

    setSelected(newSelected)
  }

  const handleSaveSchema = async () => {
    try {
      const changedPairs = pairs.filter((pair) => pair.isChanged);
      // Instead of looping over each pair, send the entire array in one request
      await axiosProvider.post(`headline-category`, {
        pairs: changedPairs.map(pair => ({ headline: pair.headline, category: pair.category }))
      });

      handleOpenSnackbar('Changed headline-category pairs saved successfully.', 'success');
    } catch (error) {
      console.error('Error saving headline-category pairs:', error);
      handleOpenSnackbar('Error saving data.', 'error');
    }
  };

  return (
    <Container>
      <Typography variant='h4' mb={2}>
        Merkmals- und Kategorienzuordnung
      </Typography>

      <Grid container spacing={4}>
        <Grid item xs={12} md={3} lg={3}>
          <Box>
            <FormControl fullWidth>
              <Select
                value={batchCategory}
                onChange={handleBatchCategoryChange}
                displayEmpty
                inputProps={{ 'aria-label': 'Without label' }}
                sx={{ bgcolor: 'white' }}>
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.name}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack direction='row' spacing={2} mt={2}>
              <Tooltip title="Neue hinzufügen">
                <IconButton aria-label='add' onClick={handleModalOpen} className='iconButtonAdd'>
                  <AddIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Stapelaktualisierung für Kategorien">
                <Button disabled={selected.length <= 0} size='small' variant='contained' color={selected.length <= 0 ? 'secondary' : 'primary'} sx={{ mt: 2, color: 'white', whiteSpace: 'nowrap' }} onClick={applyBatchCategory} endIcon={<KeyboardDoubleArrowRightIcon />}>
                  Kategorie anwenden {selected.length > 0 && `(${selected.length})`}
                </Button>
              </Tooltip>
            </Stack>
          </Box>
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          {isLoading ? (
            <CircularProgress />
          ) : (
            <Paper sx={{ maxHeight: '75vh', overflow: 'auto' }}>
              <TableContainer>
                <Table sx={{ minWidth: 250 }} aria-labelledby='tableTitle' size={'small'}>
                  <EnhancedTableHead
                    numSelected={selected.length}
                    order={order}
                    orderBy={orderBy}
                    onSelectAllClick={handleSelectAllClick}
                    onRequestSort={handleRequestSort}
                    rowCount={headLines.length}
                  />
                  <TableBody>
                    {headLines.map((row, index) => {
                      const isItemSelected = isSelected(row.name)
                      const labelId = `enhanced-table-checkbox-${index}`

                      return (
                        <TableRow
                          hover
                          aria-checked={isItemSelected}
                          tabIndex={-1}
                          key={row.id}
                          selected={isItemSelected}
                          sx={{ cursor: 'pointer' }}>
                          <TableCell padding='checkbox'>
                            <Checkbox
                              color='primary'
                              checked={isItemSelected}
                              inputProps={{
                                'aria-labelledby': labelId
                              }}
                              onClick={(event) => handleCheckboxClick(event, row.name)}
                            />
                          </TableCell>
                          <TableCell component='th' id={labelId} scope='row' padding='none'>
                            {row.name}
                          </TableCell>
                          <TableCell align='left'>
                            <Select
                              value={pairs.find((pair) => pair.headline === row.name)?.category ?? ''}
                              onChange={(event) => handleCategoryChange(row.name, event.target.value)}>
                              {categories.map((category) => (
                                <MenuItem key={category.id} value={category.name}>
                                  {category.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Grid>
        <Grid item xs={12} md={3} lg={3}>
          <Button variant='contained' color='success' onClick={handleSaveSchema} endIcon={
            <SvgIcon>
              {/* credit: plus icon from https://heroicons.com/ */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 512 512"
                strokeWidth={1.5}
                stroke="currentColor"
              >
                <path
                  d="M0 0 C1.12 -0.015 1.12 -0.015 2.27 -0.03 C19.20 -0.20 35.40 0.13 51.93 4.24 C52.68 4.41 53.42 4.59 54.19 4.77 C97.83 15.37 136.95 45.06 160.37 83.14 C169.68 98.69 177.39 115.39 180.93 133.24 C182.21 133.40 183.49 133.57 184.81 133.74 C210.35 138.89 233.54 158.22 247.99 179.24 C264.64 204.39 270.01 235.51 264.91 265.17 C258.64 294.98 240.13 320.91 214.89 337.64 C195.13 350.20 173.66 354.58 150.58 354.43 C148.82 354.43 147.07 354.43 145.31 354.43 C140.73 354.42 136.14 354.40 131.56 354.38 C126.86 354.36 122.17 354.35 117.48 354.34 C108.30 354.31 99.12 354.28 89.93 354.24 C89.93 343.68 89.93 333.12 89.93 322.24 C96.57 322.16 96.57 322.16 103.34 322.09 C106.91 322.05 110.49 321.99 114.06 321.95 C117.81 321.90 121.56 321.86 125.31 321.82 C157.03 321.48 186.67 320.94 210.93 297.24 C226.86 280.19 233.00 258.99 232.31 235.99 C231.46 219.74 224.50 203.62 213.93 191.24 C213.51 190.67 213.09 190.11 212.65 189.53 C201.34 174.99 183.13 167.57 165.62 163.96 C160.79 162.93 157.44 161.95 153.93 158.24 C151.02 153.54 149.89 148.61 148.62 143.30 C143.12 121.58 133.13 102.52 118.93 85.24 C118.48 84.68 118.02 84.13 117.55 83.55 C94.02 55.42 58.01 39.27 22.99 35.49 C20.31 35.38 18.62 35.30 16.93 35.24 C15.49 35.18 15.49 35.18 14.02 35.12 C-23.84 34.32 -60.95 48.59 -88.46 74.61 C-116.12 101.64 -131.96 137.90 -132.60 176.63 C-132.78 182.52 -134.64 186.34 -138.13 190.99 C-143.03 194.75 -148.43 194.91 -154.44 195.66 C-172.79 198.01 -188.05 205.19 -199.69 219.80 C-210.60 234.86 -213.92 251.48 -211.64 269.66 C-208.33 286.39 -199.09 300.35 -185.29 310.20 C-168.16 321.06 -150.97 321.37 -131.37 321.55 C-129.57 321.58 -127.77 321.60 -125.97 321.62 C-121.25 321.67 -116.54 321.72 -111.83 321.77 C-107.01 321.82 -102.19 321.88 -97.37 321.93 C-87.94 322.04 -78.50 322.14 -69.07 322.24 C-69.07 332.80 -69.07 343.36 -69.07 354.24 C-81.16 354.33 -93.25 354.40 -105.34 354.45 C-110.96 354.47 -116.58 354.49 -122.20 354.54 C-127.63 354.58 -133.06 354.61 -138.50 354.62 C-140.56 354.63 -142.62 354.64 -144.68 354.66 C-172.29 354.94 -196.15 347.84 -216.70 328.57 C-235.16 310.38 -245.89 287.88 -246.54 261.92 C-246.75 233.49 -238.31 209.78 -218.07 189.24 C-205.09 176.24 -188.21 166.74 -170.07 163.24 C-168.75 163.24 -167.43 163.24 -166.07 163.24 C-166.05 162.49 -166.04 161.75 -166.02 160.98 C-164.58 127.94 -151.29 95.19 -131.07 69.24 C-130.40 68.35 -129.73 67.46 -129.04 66.54 C-100.30 29.63 -58.26 7.19 -12.29 0.71 C-8.20 0.21 -4.12 0.04 0 0 Z"
                  transform="translate(246.07,54.76)"
                  fill="#FFFFFF"
                />
                <path
                  d="M0 0 C1.12 -0.02 1.12 -0.02 3.03 -0.06 C9.60 1.02 14.12 6.21 18.62 10.77 C19.24 11.39 19.87 12.02 20.51 12.66 C22.56 14.71 24.61 16.77 26.65 18.83 C28.07 20.26 29.50 21.69 30.93 23.12 C34.67 26.87 38.41 30.62 42.14 34.38 C45.96 38.22 49.78 42.05 53.61 45.88 C61.10 53.40 68.58 60.91 76.06 68.44 C74.36 72.06 72.35 74.38 69.49 77.20 C68.60 78.09 67.70 78.98 66.78 79.90 C65.84 80.82 64.90 81.74 63.94 82.69 C62.98 83.63 62.03 84.58 61.07 85.52 C58.74 87.83 56.40 90.14 54.06 92.44 C39.30 85.06 29.73 70.11 18.06 58.44 C17.73 107.94 17.40 157.44 17.06 208.44 C6.17 208.44 -4.72 208.44 -15.94 208.44 C-16.43 133.69 -16.43 133.69 -16.94 57.44 C-28.49 68.99 -40.04 80.54 -51.94 92.44 C-57.29 87.85 -62.41 83.24 -67.44 78.31 C-68.18 77.61 -68.92 76.91 -69.68 76.18 C-70.37 75.50 -71.07 74.82 -71.78 74.12 C-72.73 73.20 -72.73 73.20 -73.69 72.27 C-74.94 70.44 -74.94 70.44 -74.67 68.50 C-73.44 65.04 -70.68 62.78 -68.12 60.25 C-67.51 59.64 -66.91 59.03 -66.28 58.40 C-64.27 56.38 -62.25 54.38 -60.23 52.37 C-58.83 50.97 -57.44 49.57 -56.04 48.17 C-53.10 45.24 -50.16 42.31 -47.22 39.38 C-43.45 35.64 -39.69 31.88 -35.94 28.12 C-33.05 25.23 -30.16 22.34 -27.26 19.45 C-25.87 18.07 -24.49 16.69 -23.10 15.30 C-21.16 13.36 -19.22 11.43 -17.27 9.50 C-16.70 8.93 -16.13 8.35 -15.55 7.76 C-10.77 3.05 -6.98 0.01 0 0 Z"
                  transform="translate(255.94,248.56)"
                  fill="#FFFFFF"
                />

              </svg>
            </SvgIcon>}>
            Schema speichern
          </Button>
        </Grid>
      </Grid>
      <Dialog open={isModalOpen} onClose={handleModalClose}>
        <DialogTitle>Neue Kategorie hinzufügen</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin='dense'
            id='name'
            label='Kategoriename'
            type='text'
            fullWidth
            variant='standard'
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            error={!!errorAdding}
            helperText={errorAdding}
            InputProps={{
              endAdornment: isAdding ? <CircularProgress size={24} /> : null
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleModalClose}>Abbrechen</Button>
          <Button onClick={handleSaveNewCategory} disabled={isAdding}>
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'right' }} open={openSnackbar} autoHideDuration={1200} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  )
}
